import React, { useEffect, useState } from "react";
import { AppDispatch } from "../helpers/appDispatch";
import { useDispatch } from "react-redux";
import { appSelector } from "../helpers/appSelector";
import { getCurrentAcademicYears } from "../helpers/functions";
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { Helmet } from "react-helmet";
import { Content } from "antd/lib/layout/layout";
import { InfoCircleOutlined } from "@ant-design/icons";
import { getHalls } from "../store/halls";
import { getHallFloors } from "../store/hallFloors";
import { getHallRooms } from "../store/hallRooms";
import { getStudentsRequest } from "../store/students";
import { getStudentsInRoom } from "../store/studentRoomAssignments/actions";
import { Hall, HallFloor, HallRoom, Student } from "../interfaces";
import AssignStudentToRoom from "../components/hallStudents/AssignStudentToRoom";

const { Title } = Typography;
const { Option } = Select;

const HallStudents = () => {
  const dispatch: AppDispatch = useDispatch();

  const { school } = appSelector((state) => state.schoolStore);
  const { halls, loading: loadingHalls } = appSelector(
    (state) => state.hallsStore
  );
  const { floors, loading: loadingFloors } = appSelector(
    (state) => state.hallFloorStore
  );
  const { rooms, loading: loadingRooms } = appSelector(
    (state) => state.hallRoomStore
  );
  const { students, loading: loadingStudents } = appSelector(
    (state) => state.studentsStore
  );
  const { studentsInRoom, isLoadingStudents: loadingRoomAssignments } =
    appSelector((state) => state.studentRoomAssignmentStore);

  const { currentYear } = getCurrentAcademicYears();
  const [selectedAcademicYear, setSelectedAcademicYear] =
    useState<string>("All");

  // Generate academic year options dynamically
  const generateAcademicYearOptions = (): string[] => {
    const options: string[] = ["All"];
    for (let i = -2; i <= 2; i++) {
      const year = currentYear + i;
      const next = year + 1;
      options.push(`${year}-${next}`);
    }
    return options;
  };

  const academicYearOptions = generateAcademicYearOptions();
  const [selectedHallId, setSelectedHallId] = useState<string>("");
  const [selectedFloorId, setSelectedFloorId] = useState<string>("");
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");
  const [currentRoom, setCurrentRoom] = useState<HallRoom | null>(null);

  useEffect(() => {
    if (school?.id) {
      dispatch(getHalls(school.id.toString()));
    }
  }, [dispatch, school]);

  useEffect(() => {
    if (school?.id && currentRoom) {
      // Load all students when room is selected (without academic year filter)
      dispatch(
        getStudentsRequest({
          school_id: school.id.toString(),
        })
      );
    }
  }, [dispatch, school, currentRoom]);

  useEffect(() => {
    if (currentRoom) {
      // Fetch room assignments - pass academic year only if not "All"
      const payload: any = { roomId: currentRoom.id.toString() };
      if (selectedAcademicYear !== "All") {
        payload.academicYear = selectedAcademicYear;
      }
      dispatch(getStudentsInRoom(payload));
    }
  }, [dispatch, currentRoom, selectedAcademicYear]);

  const handleHallChange = (hallId: string) => {
    setSelectedHallId(hallId);
    setSelectedFloorId("");
    setSelectedRoomId("");
    setCurrentRoom(null);
    dispatch(getHallFloors(hallId));
  };

  const handleFloorChange = (floorId: string) => {
    setSelectedFloorId(floorId);
    setSelectedRoomId("");
    setCurrentRoom(null);
    dispatch(
      getHallRooms({
        hall_id: selectedHallId,
        floor_id: floorId,
      })
    );
  };

  const handleRoomChange = (roomId: string) => {
    setSelectedRoomId(roomId);
    const room = rooms.find((r) => r.id.toString() === roomId);
    setCurrentRoom(room || null);
  };

  const filteredFloors = floors.filter(
    (floor) => floor.hall?.id.toString() === selectedHallId
  );

  const filteredRooms = rooms.filter(
    (room) => room.floor?.id.toString() === selectedFloorId
  );

  return (
    <>
      <Helmet>
        <title>Admissions Ghana | Hall Students Management</title>
      </Helmet>
      <div className="padding-box">
        <Content className="site-layout-background site-content">
          <div className="margin-top">
            <Title level={4}>Hall Students Management</Title>
            <Divider />
            <Form colon={false}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Hall">
                    <Select
                      placeholder="Select Hall"
                      style={{ width: "100%" }}
                      value={selectedHallId}
                      onChange={handleHallChange}
                      loading={loadingHalls}
                    >
                      {halls.map((hall) => (
                        <Option key={hall.id} value={hall.id.toString()}>
                          {hall.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="Floor">
                    <Select
                      placeholder="Select Floor"
                      style={{ width: "100%" }}
                      value={selectedFloorId}
                      onChange={handleFloorChange}
                      disabled={!selectedHallId}
                      loading={loadingFloors}
                    >
                      {filteredFloors.map((floor) => (
                        <Option key={floor.id} value={floor.id.toString()}>
                          {floor.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="Room">
                    <Select
                      placeholder="Select Room"
                      style={{ width: "100%" }}
                      value={selectedRoomId}
                      onChange={handleRoomChange}
                      disabled={!selectedFloorId}
                      loading={loadingRooms}
                    >
                      {filteredRooms.map((room) => (
                        <Option key={room.id} value={room.id.toString()}>
                          {room.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>{" "}
            {currentRoom && (
              <>
                <Divider />
                <Alert
                  message={`Managing Students for Room: ${currentRoom.name}`}
                  description={`Floor: ${currentRoom.floor?.name} | Hall: ${currentRoom.floor?.hall?.name}`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={24}>
                    <Form.Item label="Filter by Academic Year">
                      <Select
                        value={selectedAcademicYear}
                        onChange={setSelectedAcademicYear}
                        style={{ width: 200 }}
                      >
                        {academicYearOptions.map((year) => (
                          <Option key={year} value={year}>
                            {year}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <AssignStudentToRoom
                  room={currentRoom}
                  students={students}
                  loading={loadingStudents}
                  schoolId={school?.id?.toString() || ""}
                  roomAssignments={studentsInRoom}
                  loadingRoomAssignments={loadingRoomAssignments}
                />
              </>
            )}
          </div>
        </Content>
      </div>
    </>
  );
};

export default HallStudents;
